import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { useAuth, useCan } from "@/context/AuthContext";
import { NoAccess } from "@/components/NoAccess";

const BADGE = { New: "bg-emerald-100 text-emerald-800", Update: "bg-sky-100 text-sky-800", Error: "bg-rose-100 text-rose-700" };
const LIMIT = 400;

export default function ImportProducts() {
  const can = useCan();
  return can("products") ? <ImportScreen /> : <NoAccess what="bulk import" />;
}

function Step({ n, title, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-3">
        <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-900 font-mono text-xs font-bold text-amber-400">{n}</span>
        <h2 className="font-display text-lg font-bold text-slate-900">{title}</h2>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function UploadZone({ onFile, busy }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); e.dataTransfer.files[0] && onFile(e.dataTransfer.files[0]); }}
      className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-10 text-center transition-colors ${drag ? "border-amber-500 bg-amber-50" : "border-slate-300 hover:border-slate-500"}`}
      data-testid="import-dropzone"
    >
      {busy ? <Loader2 className="h-7 w-7 animate-spin text-slate-500" /> : <Upload className="h-7 w-7 text-slate-500" />}
      <span className="text-sm font-semibold text-slate-800">{busy ? "Reading file…" : "Drop your filled template here, or click to choose"}</span>
      <span className="font-mono text-[11px] text-slate-400">.xlsx or .csv · up to 5,000 rows</span>
      <input type="file" className="sr-only" disabled={busy} data-testid="import-file-input" onChange={(e) => { e.target.files[0] && onFile(e.target.files[0]); e.target.value = ""; }} />
    </label>
  );
}

function PreviewTable({ rows, pharmacy }) {
  const [filter, setFilter] = useState("All");
  const shown = useMemo(() => rows.filter((r) => filter === "All" || r.status === filter), [rows, filter]);
  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {["All", "New", "Update", "Error"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`import-filter-${f.toLowerCase()}`} className={`h-8 rounded-full px-3 text-xs font-bold ${filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{f}</button>
        ))}
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[720px] text-left text-sm" data-testid="import-preview-table">
          <thead className="bg-slate-50 font-mono text-[10.5px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Row</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Name</th><th className="px-3 py-2">Barcode</th><th className="px-3 py-2 text-right">Price</th><th className="px-3 py-2 text-right">Tax %</th>
              {pharmacy && <><th className="px-3 py-2">Batch</th><th className="px-3 py-2">Expiry</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">Branch</th></>}
              <th className="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {shown.slice(0, LIMIT).map((r) => (
              <tr key={r.row} data-testid={`import-row-${r.row}`} className={r.status === "Error" ? "bg-rose-50/40" : ""}>
                <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.row}</td>
                <td className="px-3 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${BADGE[r.status]}`} data-testid={`import-row-status-${r.row}`}>{r.status}</span></td>
                <td className="max-w-[220px] truncate px-3 py-2 font-medium text-slate-900">{r.name || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.barcode || <span className="text-slate-400">auto</span>}</td>
                <td className="money px-3 py-2 text-right">{r.price || "—"}</td>
                <td className="money px-3 py-2 text-right">{r.tax_rate ?? <span className="text-slate-400">default</span>}</td>
                {pharmacy && <><td className="px-3 py-2 font-mono text-xs">{r.batch_no || "—"}</td><td className="px-3 py-2 font-mono text-xs">{r.expiry || "—"}</td><td className="money px-3 py-2 text-right">{r.qty ?? "—"}</td><td className="px-3 py-2 text-xs">{r.branch || "—"}</td></>}
                <td className={`px-3 py-2 text-xs ${r.status === "Error" ? "font-semibold text-rose-700" : "text-slate-500"}`} data-testid={`import-row-reason-${r.row}`}>{r.status === "Error" ? r.reason : r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {shown.length > LIMIT && <div className="mt-2 font-mono text-xs text-slate-500">Showing the first {LIMIT} of {shown.length} rows.</div>}
    </div>
  );
}

function Counts({ c }) {
  const items = [["New", c.new, "new"], ["Update", c.update, "update"], ["Error", c.error, "error"]];
  return (
    <div className="grid grid-cols-3 gap-2">
      {items.map(([label, v, id]) => (
        <div key={id} className={`rounded-xl px-4 py-3 ${BADGE[label]}`}>
          <div className="font-mono text-[10.5px] uppercase tracking-widest opacity-80">{label}</div>
          <div className="money text-2xl font-extrabold" data-testid={`import-count-${id}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function Result({ r, onAgain }) {
  return (
    <div className="space-y-4" data-testid="import-result">
      <div className="flex items-center gap-2 font-display text-xl font-bold text-slate-900"><CheckCircle2 className="h-6 w-6 text-emerald-600" /> Import finished</div>
      <div className="grid grid-cols-3 gap-2">
        {[["Created", r.created, "created"], ["Updated", r.updated, "updated"], ["Skipped", r.skipped, "skipped"]].map(([l, v, id]) => (
          <div key={id} className="rounded-xl border border-slate-200 px-4 py-3">
            <div className="font-mono text-[10.5px] uppercase tracking-widest text-slate-500">{l}</div>
            <div className="money text-2xl font-extrabold text-slate-900" data-testid={`import-result-${id}`}>{v}</div>
          </div>
        ))}
      </div>
      <div className="font-mono text-xs text-slate-500" data-testid="import-result-detail">
        {r.products_created} products created · {r.products_updated} updated{r.batches_created + r.batches_updated > 0 ? ` · ${r.batches_created} batches added · ${r.batches_updated} batches updated` : ""}
      </div>
      <div className="flex flex-wrap gap-2">
        {r.skipped > 0 && (
          <button onClick={() => downloadFile(`/import/${r.id}/errors`, "import-errors.csv").catch((e) => toast.error(errMsg(e)))} data-testid="import-errors-download-button" className="flex h-11 items-center gap-2 rounded-xl border border-rose-300 px-4 text-sm font-bold text-rose-700 hover:bg-rose-50">
            <Download className="h-4 w-4" /> Download error list
          </button>
        )}
        <Link to="/products" data-testid="import-view-products-link" className="flex h-11 items-center rounded-xl bg-slate-900 px-4 text-sm font-bold text-white hover:bg-slate-800">View products</Link>
        <button onClick={onAgain} data-testid="import-again-button" className="h-11 rounded-xl border border-slate-200 px-4 text-sm font-bold text-slate-700 hover:border-slate-900">Import another file</button>
      </div>
    </div>
  );
}

function ImportScreen() {
  const { session } = useAuth();
  const pharmacy = session.business.business_type === "pharmacy";
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    setBusy(true);
    setError("");
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      setPreview((await api.post("/import/preview", fd)).data);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    try {
      setResult((await api.post(`/import/${preview.id}/confirm`)).data);
      setPreview(null);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { setPreview(null); setResult(null); setError(""); };
  const importable = preview ? preview.counts.new + preview.counts.update : 0;

  return (
    <div className="h-full overflow-y-auto" data-testid="import-page">
      <div className="mx-auto max-w-5xl space-y-5 px-4 py-8 sm:px-8">
        <div>
          <div className="eyebrow">Catalogue · bulk upload</div>
          <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Import products</h1>
          <p className="mt-1 text-sm text-slate-500">{pharmacy ? "One row per batch. Rows with the same barcode become batches of one medicine." : "An existing barcode updates that product's name, price and tax."}</p>
        </div>
        <Step n="1" title="Download the template">
          <button onClick={() => downloadFile("/import/template", "smart-in-products-template.xlsx").catch((e) => toast.error(errMsg(e)))} data-testid="import-template-button" className="flex h-11 items-center gap-2 rounded-xl border border-slate-900 px-4 text-sm font-bold text-slate-900 hover:bg-slate-900 hover:text-white">
            <FileSpreadsheet className="h-4 w-4" /> Download {pharmacy ? "pharmacy" : "retail"} template (.xlsx)
          </button>
          <p className="mt-2 font-mono text-[11px] text-slate-500">Columns: Name, Barcode, Price, Tax rate %{pharmacy ? ", Batch No, Expiry (YYYY-MM-DD), Quantity, Branch" : ""}</p>
        </Step>
        <Step n="2" title="Upload and check the preview">
          {result ? <Result r={result} onAgain={reset} /> : (
            <div className="space-y-4">
              {!preview && <UploadZone onFile={upload} busy={busy} />}
              {error && <div className="rounded-lg bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700" data-testid="import-error">{error}</div>}
              {preview && (
                <>
                  <div className="font-mono text-xs text-slate-500" data-testid="import-preview-filename">{preview.filename} · {preview.counts.total} rows</div>
                  <Counts c={preview.counts} />
                  <PreviewTable rows={preview.rows} pharmacy={pharmacy} />
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={confirm} disabled={!importable || busy} data-testid="import-confirm-button" className="flex h-12 items-center gap-2 rounded-xl bg-amber-500 px-5 font-bold text-slate-950 hover:bg-amber-400 disabled:bg-slate-200 disabled:text-slate-400">
                      {busy && <Loader2 className="h-4 w-4 animate-spin" />} Import {importable} row{importable === 1 ? "" : "s"}{preview.counts.error ? ` · skip ${preview.counts.error}` : ""}
                    </button>
                    <button onClick={reset} data-testid="import-choose-another-button" className="h-12 rounded-xl border border-slate-200 px-4 text-sm font-bold text-slate-700 hover:border-slate-900">Choose another file</button>
                  </div>
                </>
              )}
            </div>
          )}
        </Step>
      </div>
    </div>
  );
}
